import request from 'services/axios'

/**
 * Server-side switches an admin can flip without a deploy.
 *
 * `setting.models.Default` is a key/value table the Django admin edits, and
 * `/settings/defaults/` serves every active row to any signed-in caller. The
 * backend has read it for years — the gateway kill-switch, the cashout
 * kill-switch, the marketing banner — but the clients never did, so anything
 * an admin turned off stayed visible here until somebody shipped a build.
 *
 * `support_bot_enabled` is the first key this reads, for exactly that reason:
 * the assistant could be closed server-side while «پشتیبانی آنلاین» sat in the
 * support menu, and the only way to find out was to tap it and collect a 503.
 */

const URL = '/settings/defaults/'

/** '0' is off. Everything else — including a key that is not there — is on. */
export const SUPPORT_BOT_ENABLED = 'support_bot_enabled'

export interface IAppDefault {
    key: string
    value: string
    type: string
}

export type TAppDefaults = Record<string, string>

/**
 * `page_size=1000` because this endpoint paginates at 100 and the answer has
 * to be complete to be usable: a key that fell onto page two would read as
 * absent, and absent means "on" everywhere below. The table holds a few dozen
 * rows, so one page is the whole thing either way.
 */
export const getAppDefaults = async (): Promise<TAppDefaults> => {
    const { data } = await request.get<
        { results?: IAppDefault[] } | IAppDefault[]
    >(URL, { params: { page_size: 1000 } })

    const rows = Array.isArray(data) ? data : data?.results ?? []
    const map: TAppDefaults = {}
    rows.forEach((row) => {
        if (row?.key) map[row.key] = row.value
    })
    return map
}

/**
 * Fail open, deliberately, and in three different ways: an undefined map (the
 * request is in flight or failed), a missing key (an environment this
 * migration has not reached) and any value other than '0' all read as on.
 *
 * The switch exists to take a broken assistant away, and a network blip is not
 * evidence of one. The opposite default would mean a slow connection can hide
 * the support menu's top row from somebody who needs it.
 */
export const defaultIsOn = (defaults: TAppDefaults | undefined, key: string) =>
    defaults?.[key] !== '0'
